import { Box, Flex, Text } from "@chakra-ui/react";
import React, { useMemo } from "react";
import { buildExamReport } from "./exam-mark-report";

const CHIP = {
  correct: { bg: "#E5F7EC", color: "#146B3A", border: "#BBE0CA" },
  partial: { bg: "#FFF4DE", color: "#B46E14", border: "#F2D6A0" },
  incorrect: { bg: "#FDE8E8", color: "#C0392B", border: "#F4C6C6" },
};

const SECTIONS = [
  { key: "strengths", label: "Strengths", bg: "#E5F7EC", color: "#1F8A4C" },
  { key: "errors", label: "Error to Review", bg: "#FFF6E8", color: "#B46E10" },
  { key: "priority", label: "Main Improvement Priority", bg: "#E7F1FB", color: "#1D4E89" },
  { key: "scope", label: "Scope", bg: "#F1F3F5", color: "#4B5563" },
  { key: "examiner", label: "Examiner Feedback", bg: "#F3E9FB", color: "#6D28D9" },
];

function MarkIcon({ positive, color }) {
  return (
    <Box as="span" color={color} fontWeight="900" lineHeight="1" aria-hidden="true">
      {positive ? "✓" : "✕"}
    </Box>
  );
}

const ExamResultsCover = ({
  studentName,
  examName,
  awarded,
  max,
  hideScore = false,
  questions = [],
  overallComment = "",
}) => {
  const report = useMemo(
    () =>
      buildExamReport({
        questions,
        examName,
        studentName,
        awarded,
        max,
        hideScore,
        overallComment,
      }),
    [questions, examName, studentName, awarded, max, hideScore, overallComment]
  );

  const stats = report.hideScore
    ? [
        { label: "Questions marked", value: String(report.questionsMarked) },
        { label: "Full-mark questions", value: String(report.fullMarkCount) },
      ]
    : [
        { label: "Total score", value: report.scoreLabel },
        { label: "Percentage", value: report.percentLabel },
        { label: "Questions marked", value: String(report.questionsMarked) },
        { label: "Full-mark questions", value: String(report.fullMarkCount) },
      ];

  return (
    <Flex direction="column" w="100%" minH="100%" bg="#E8F1FA" px={{ base: 4, md: 6 }} py={{ base: 4, md: 5 }} gap={4}>
      <Flex justify="space-between" align="flex-start" gap={4}>
        <Box minW={0}>
          <Text
            fontWeight="900"
            fontSize={{ base: "lg", md: "xl" }}
            color="#1B3A5F"
            letterSpacing="0.03em"
            textTransform="uppercase"
            lineHeight="1.15"
          >
            {report.examName}
          </Text>
          <Text fontWeight="800" fontSize="md" color="#1A2332" mt={1}>
            Final Report
          </Text>
          <Text fontSize="10px" fontWeight="800" letterSpacing="0.08em" color="#5C6B7A" mt={1}>
            STUDENT MARKED WORK ANALYSIS
          </Text>
          {report.studentName ? (
            <Text fontSize="sm" color="#1A2332" mt={1}>
              {report.studentName}
            </Text>
          ) : null}
        </Box>
        <Text fontSize="sm" fontWeight="600" color="#1B3A5F" textAlign="right" flexShrink={0}>
          {report.tagline}
        </Text>
      </Flex>

      <Flex gap={3} wrap="wrap">
        {stats.map((stat) => (
          <Box
            key={stat.label}
            flex="1"
            minW={report.hideScore ? "140px" : "120px"}
            bg="#E5F7EC"
            border="1px solid"
            borderColor="#BBE0CA"
            borderRadius="lg"
            px={3}
            py={3}
          >
            <Text fontSize="10px" fontWeight="800" letterSpacing="0.06em" textTransform="uppercase" color="#1F8A4C">
              {stat.label}
            </Text>
            <Text fontWeight="900" fontSize={{ base: "xl", md: "2xl" }} color="#146B3A" lineHeight="1.15" mt={1}>
              {stat.value}
            </Text>
          </Box>
        ))}
      </Flex>

      <Box>
        <Flex justify="space-between" align="baseline" gap={3} mb={2}>
          <Text fontWeight="800" color="#1B3A5F">
            Questions Summary
          </Text>
          <Text fontSize="10px" fontWeight="800" letterSpacing="0.06em" color="#5C6B7A">
            QUESTION-BY-QUESTION RESULTS
          </Text>
        </Flex>
        <Flex wrap="wrap" gap={2}>
          {report.chips.map((chip, index) => {
            const colors = CHIP[chip.tone] || CHIP.partial;
            return (
              <Flex
                key={`${chip.id}-${index}`}
                align="center"
                gap={1}
                bg={colors.bg}
                color={colors.color}
                border="1px solid"
                borderColor={colors.border}
                borderRadius="md"
                px={2}
                py={1}
              >
                <MarkIcon positive={chip.tone !== "incorrect"} color={colors.color} />
                <Text fontSize="xs" fontWeight="800">
                  {chip.id}
                </Text>
                <Text fontSize="xs" fontWeight="700">
                  {chip.awarded}/{chip.max}
                </Text>
              </Flex>
            );
          })}
        </Flex>
      </Box>

      <Flex direction="column" gap={2}>
        {SECTIONS.map((section) => (
          <Flex
            key={section.key}
            bg={section.bg}
            borderRadius="lg"
            overflow="hidden"
            align="stretch"
          >
            <Box w="4px" bg={section.color} flexShrink={0} />
            <Flex
              direction={{ base: "column", md: "row" }}
              gap={{ base: 1, md: 4 }}
              px={3}
              py={3}
              flex="1"
              minW={0}
            >
              <Text
                fontSize="sm"
                fontWeight="800"
                color={section.color}
                w={{ md: "190px" }}
                flexShrink={0}
              >
                {section.label}
              </Text>
              <Text fontSize="sm" color="#1A2332" lineHeight="1.45">
                {report[section.key]}
              </Text>
            </Flex>
          </Flex>
        ))}
      </Flex>

      <Flex
        mt="auto"
        bg="#E5F7EC"
        borderRadius="lg"
        px={4}
        py={3}
        align="center"
        justify="space-between"
        gap={3}
      >
        <Flex align="center" gap={2} minW={0}>
          <Text color="#1F8A4C" fontWeight="900" aria-hidden="true">
            ✓
          </Text>
          <Text fontSize="sm" color="#146B3A">
            {report.consistent}
          </Text>
        </Flex>
        <Text fontWeight="800" color="#1F8A4C" flexShrink={0}>
          {report.praise}
        </Text>
      </Flex>
    </Flex>
  );
};

export default ExamResultsCover;
